import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Shield, TrendingUp, Clock, Filter } from 'lucide-react';
import { useEnhancedContentAnalysis } from '@/hooks/useEnhancedContentAnalysis';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface ActivityItem {
  id: string;
  type: 'filter' | 'analysis' | 'alert' | 'trend';
  timestamp: Date;
  platform: string;
  content: string;
  score?: number;
  sentiment?: string;
  filtered?: boolean;
  author?: string;
}

export const ActivityFeed: React.FC = () => {
  const { user } = useAuth();
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [isLive, setIsLive] = useState(true);
  const { analyzeContentEnhanced, loading } = useEnhancedContentAnalysis();

  // Load real activity data from database
  useEffect(() => {
    if (!isLive || !user) return;

    const loadRealActivity = async () => {
      try {
        // Get recent filtered content and AI analysis
        const [filteredData, analysisData] = await Promise.all([
          supabase
            .from('filtered_content')
            .select('*')
            .eq('user_id', user.id)
            .gte('filtered_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
            .order('filtered_at', { ascending: false })
            .limit(10),
          
          supabase
            .from('ai_content_analysis')
            .select('*')
            .eq('user_id', user.id)
            .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
            .order('created_at', { ascending: false })
            .limit(10)
        ]);

        const realActivities: ActivityItem[] = [];

        // Add filtered content activities
        if (filteredData.data) {
          filteredData.data.forEach(item => {
            realActivities.push({
              id: `filter_${item.id}`,
              type: 'filter',
              timestamp: new Date(item.filtered_at),
              platform: item.platform,
              content: item.content_text || 'Content filtered',
              sentiment: 'negative',
              filtered: true,
              author: item.content_author || 'Unknown'
            });
          });
        }

        // Add analysis activities
        if (analysisData.data) {
          analysisData.data.forEach(item => {
            realActivities.push({
              id: `analysis_${item.id}`,
              type: 'analysis',
              timestamp: new Date(item.created_at),
              platform: item.platform,
              content: `${item.content_analyzed} items analyzed`,
              score: 0.8,
              sentiment: 'positive',
              filtered: false,
              author: 'AI System'
            });
          });
        }

        // Sort by timestamp and keep last 20
        const sortedActivities = realActivities
          .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
          .slice(0, 20);

        setActivities(sortedActivities);
      } catch (error) {
        console.error('Error loading real activity:', error);
      }
    };

    loadRealActivity();
    const interval = setInterval(loadRealActivity, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [isLive, user]);

  const getActivityIcon = (type: string, filtered?: boolean) => {
    switch (type) {
      case 'filter':
        return <Shield className="h-4 w-4 text-red-500" />;
      case 'analysis':
        return filtered ? <AlertTriangle className="h-4 w-4 text-yellow-500" /> : <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'alert':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getSentimentColor = (sentiment?: string) => {
    switch (sentiment) {
      case 'positive': return 'bg-green-100 text-green-800 border-green-200';
      case 'negative': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <Card className="h-[600px]">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Live Activity Feed</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant={isLive ? "default" : "outline"}
              size="sm"
              onClick={() => setIsLive(!isLive)}
              className="h-8 px-3"
            >
              {isLive ? (
                <>
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse" />
                  Live
                </>
              ) : (
                <>
                  <Clock className="h-3 w-3 mr-2" />
                  Paused
                </>
              )}
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Real-time content analysis and filtering results
        </p>
      </CardHeader>
      
      <CardContent className="p-0">
        <ScrollArea className="h-[500px] px-6">
          {activities.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Filter className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No activity yet. {isLive ? 'Waiting for content...' : 'Start live feed to see activity.'}</p>
            </div>
          ) : (
            <div className="space-y-4 pb-4">
              {activities.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex-shrink-0 mt-1">
                    {getActivityIcon(activity.type, activity.filtered)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="h-5 text-xs font-medium">
                        {activity.platform}
                      </Badge>
                      <Badge variant="outline" className="h-5 text-xs">
                        @{activity.author}
                      </Badge>
                      {activity.sentiment && (
                        <Badge className={`h-5 text-xs border ${getSentimentColor(activity.sentiment)}`}>
                          {activity.sentiment}
                        </Badge>
                      )}
                      {activity.filtered && (
                        <Badge className="h-5 text-xs bg-red-100 text-red-800 border-red-200">
                          Filtered
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-sm text-foreground mb-2 line-clamp-2">
                      {activity.content}
                    </p>
                    
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>
                        {activity.timestamp.toLocaleTimeString()}
                      </span>
                      {activity.score !== undefined && (
                        <span className="font-medium">
                          Confidence: {Math.round(activity.score * 100)}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};